"use client";

import { PrivyProvider } from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit";

import { WagmiProvider } from "wagmi";
import { http, createConfig, injected } from "@wagmi/core";
import { metaMask } from "@wagmi/connectors";
import { RelayKitProvider } from "@relayprotocol/relay-kit-ui";
import {
  convertViemChainToRelayChain,
  MAINNET_RELAY_API,
} from "@relayprotocol/relay-sdk";
import { worldApp } from "@worldcoin/minikit-js/wagmi";
import {
  worldchain,
  mainnet,
  base,
  arbitrum,
  optimism,
  polygon,
} from "viem/chains";
import { useRelayChains } from "@relayprotocol/relay-kit-hooks";
import { MiniKitProvider } from "@worldcoin/minikit-js/minikit-provider";
import "@relayprotocol/relay-kit-ui/styles.css";

const worldChainOnly = [convertViemChainToRelayChain(worldchain)];

const defaultChains = [
  convertViemChainToRelayChain(worldchain),
  convertViemChainToRelayChain(mainnet),
  convertViemChainToRelayChain(base),
  convertViemChainToRelayChain(arbitrum),
  convertViemChainToRelayChain(optimism),
  convertViemChainToRelayChain(polygon),
];

const wagmiConfig = createConfig({
  chains: [worldchain, mainnet, base, arbitrum, optimism, polygon],
  transports: {
    [worldchain.id]: http("https://worldchain-mainnet.g.alchemy.com/public"),
    [mainnet.id]: http(),
    [base.id]: http(),
    [arbitrum.id]: http(),
    [optimism.id]: http(),
    [polygon.id]: http(),
  },
  connectors: [
    worldApp(), // native in World App
    injected(), // web fallback connector
    metaMask(), // MetaMask specifically
  ],
});

import {
  createDefaultAuthorizationCache,
  createDefaultChainSelector,
  registerMwa,
} from "@solana-mobile/wallet-standard-mobile";
import { isInsideCherry } from "@cherrydotfun/miniapp-sdk";
import { LocalNetworkAccessProvider } from "./LocalNetworkAccessProvider";
import useUserAgent from "../hooks/useUserAgent";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import configs from "../configs";

// WARN: ALWAYS USE STRICT MODE SO PHANTOM EMBED DOESN'T BREAK
const isCherryEmbed = isInsideCherry({ strict: true });

function getUriForAppIdentity() {
  const location = globalThis.location;
  if (!location) return;
  return `${location.protocol}//${location.host}`;
}

registerMwa({
  appIdentity: {
    name: "Crypto Fantasy League",
    uri: getUriForAppIdentity(),
    icon: "icons/icon-512x512.png", // resolves to https://myapp.io/relative/path/to/icon.png
  },
  authorizationCache: createDefaultAuthorizationCache(),
  chains: ["solana:mainnet"],
  chainSelector: createDefaultChainSelector(),
  // onWalletNotFound: createDefaultWalletNotFoundHandler(),
  onWalletNotFound: () => Promise.resolve(),
  // remoteHostAuthority: 'https://www.cfl.fun', // Include to enable remote connection option.
});

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>;
  };
};

export const CustomPrivyProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isStandalone } = useUserAgent();
  const { chains: relayChains } = useRelayChains();

  const solana = useMemo(() => {
    if (!relayChains) return null;
    return relayChains.find((c) => c.id === 792703809);
  }, [relayChains]);

  const solanaConnectors = toSolanaWalletConnectors({
    // By default, shouldAutoConnect is enabled
    shouldAutoConnect: true,
  });

  const { data: isSeeker } = useQuery({
    queryKey: ["is_seeker"],
    queryFn: async (): Promise<boolean> => {
      const userAgentData = (navigator as NavigatorWithUserAgentData)
        .userAgentData;

      if (!userAgentData?.getHighEntropyValues) {
        return false;
      }

      const response = await userAgentData.getHighEntropyValues(["model"]);
      return response.model?.includes("Seeker") ?? false;
    },
  });

  // const methods: PrivyLoginMethods = useMemo(() => {
  //   if (isAndroid) return ["wallet"];
  //   return ["google", "twitter", "wallet"];
  // }, []);

  // console.log("isCherryEmbed:", isCherryEmbed);
  // Cherry embed — minimal Privy config, auth handled by CherryAuthBridge via JWT
  // if (isCherryEmbed) {
  //   const chainId = configs.IS_TESTNET ? "solana:devnet" : "solana:mainnet";
  //   const wsUrl = configs.RPC_URL.replace("https://", "wss://");

  //   return (
  //     <PrivyProvider
  //       appId={configs.PRIVY_APP_ID}
  //       clientId={configs.PRIVY_CLIENT_ID}
  //       config={{
  //         appearance: {
  //           theme: "dark",
  //           walletChainType: "solana-only",
  //         },
  //         loginMethods: ["wallet"],
  //         embeddedWallets: {
  //           solana: {
  //             createOnLogin: "users-without-wallets",
  //           },
  //           showWalletUIs: true,
  //         },
  //         solana: {
  //           rpcs: {
  //             [chainId]: {
  //               rpc: createSolanaRpc(configs.RPC_URL),
  //               rpcSubscriptions: createSolanaRpcSubscriptions(wsUrl),
  //             },
  //           },
  //         },
  //       }}
  //     >
  //       <WagmiProvider config={wagmiConfig}>{children}</WagmiProvider>
  //     </PrivyProvider>
  //   );
  // }

  if (isStandalone && isSeeker) {
    const chainId = configs.IS_TESTNET ? "solana:devnet" : "solana:mainnet";
    const wsUrl = configs.RPC_URL.replace("https://", "wss://");

    return (
      <PrivyProvider
        appId={configs.PRIVY_APP_ID}
        // NOTE: This ensures that Privy can be used with
        // localhost origin in development environment
        clientId={configs.PRIVY_CLIENT_ID}
        config={{
          appearance: {
            theme: "dark",
            walletChainType: "solana-only",
            walletList: ["detected_solana_wallets"],
          },
          loginMethods: ["wallet"],
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
          embeddedWallets: {
            solana: {
              createOnLogin: "users-without-wallets",
            },
          },
          solana: {
            rpcs: {
              [chainId]: {
                rpc: createSolanaRpc(configs.RPC_URL),
                rpcSubscriptions: createSolanaRpcSubscriptions(wsUrl),
              },
            },
          },
        }}
      >
        <RelayKitProvider
          options={{
            appName: "Relay Demo",
            source: "cfl-app",
            appFees: [
              {
                recipient: "0x0000000000000000000000000000000000000000",
                fee: "100", // 1%
              },
            ],
            duneConfig: {
              apiKey: "sim_2PCrFFJOUO2aOYRwWKcCfRlu205pn1hr",
            },
            chains: solana ? [...worldChainOnly, solana] : worldChainOnly,
            baseApiUrl: MAINNET_RELAY_API,
            themeScheme: "dark",
          }}
        >
          <WagmiProvider config={wagmiConfig}>
            <LocalNetworkAccessProvider>{children}</LocalNetworkAccessProvider>
          </WagmiProvider>
        </RelayKitProvider>
      </PrivyProvider>
    );
  }

  const chainId = configs.IS_TESTNET ? "solana:devnet" : "solana:mainnet";
  const wsUrl = configs.RPC_URL.replace("https://", "wss://");

  return (
    <PrivyProvider
      appId={configs.PRIVY_APP_ID}
      // NOTE: This ensures that Privy can be used with
      // localhost origin in development environment
      clientId={configs.PRIVY_CLIENT_ID}
      config={{
        appearance: {
          theme: "dark",
          walletChainType: "solana-only",
          walletList: ["detected_solana_wallets", "phantom"],
          // walletList:
          //   isIOS || isSafari
          //     ? []
          //     : [
          //         "detected_solana_wallets",
          //         "phantom",
          //         "backpack",
          //         "solflare",
          //         "okx_wallet",
          //       ],
        },
        loginMethods: ["google", "twitter", "wallet"],
        externalWallets: {
          solana: {
            connectors: solanaConnectors,
          },
        },
        embeddedWallets: {
          solana: {
            createOnLogin: "users-without-wallets",
          },
        },
        solana: {
          rpcs: {
            [chainId]: {
              rpc: createSolanaRpc(configs.RPC_URL),
              rpcSubscriptions: createSolanaRpcSubscriptions(wsUrl),
            },
          },
        },
      }}
    >
      <RelayKitProvider
        options={{
          appName: "Relay Demo",
          source: "cfl-app",
          appFees: [
            {
              recipient: "0x0000000000000000000000000000000000000000",
              fee: "100", // 1%
            },
          ],
          duneConfig: {
            apiKey: "sim_2PCrFFJOUO2aOYRwWKcCfRlu205pn1hr",
          },
          chains: solana ? [...defaultChains, solana] : defaultChains,
          baseApiUrl: MAINNET_RELAY_API,
          themeScheme: "dark",
        }}
      >
        <WagmiProvider config={wagmiConfig}>
          <MiniKitProvider
            props={{
              appId: configs.WORLD_MINI_APP.ID,
              wagmiConfig,
            }}
          >
            {children}
          </MiniKitProvider>
        </WagmiProvider>
      </RelayKitProvider>
    </PrivyProvider>
  );
};
